import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        const base = `${config.apiUrl}`
        apiInstance = axios.create({
            baseURL: base,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token (optional for public endpoints)
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface DigitalProduct {
    id: string
    title: string
    slug: string
    description: string
    full_price: number
    discount_price?: number
    s3_folder_path: string
    file_count?: number
    total_size_bytes?: number
    thumbnail_url?: string
    features: string[]
    is_active: boolean
    hidden: boolean
    created_at: string
    updated_at: string
}

export interface DigitalProductFile {
    id: string
    product_id: string
    product_slug: string
    title: string
    description?: string
    category?: string
    original_filename: string
    file_size: number
    content_type: string
    file_type: string
    sort_order: number
    download_url: string
    download_count: number
    created_at: string
}

export interface DigitalProductListData {
    products: DigitalProduct[]
    total: number
}

export interface DigitalProductPricing {
    full_price: number
    discount_price?: number
    is_sale_active: boolean
    display_price: number
}

export const digitalProductService = {
    getAllProducts,
    getProductBySlug,
    getProductPricing,
}

/**
 * Get all active digital products (public)
 */
async function getAllProducts(): Promise<ApiResponse<DigitalProductListData>> {
    try {
        const response = await getApiInstance().get('/digital-products')

        let products: DigitalProduct[] = []
        let total = 0

        if (response.data?.success && response.data?.data) {
            products = response.data.data.products || []
            total = response.data.data.total || 0
        } else if (response.data?.data) {
            products = response.data.data.products || response.data.data || []
            total = response.data.data.total || products.length
        }

        return {
            success: true,
            data: { products, total }
        }
    } catch (error: any) {
        console.error('Error fetching digital products:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch digital products'
        }
    }
}

/**
 * Get a single digital product by slug (public)
 */
async function getProductBySlug(slug: string): Promise<ApiResponse<DigitalProduct>> {
    try {
        const response = await getApiInstance().get(`/digital-products/${slug}`)
        const product = response.data?.data || response.data

        if (!product) {
            return {
                success: false,
                error: 'Digital product not found'
            }
        }

        return {
            success: true,
            data: product
        }
    } catch (error: any) {
        console.error('Error fetching digital product:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch digital product'
        }
    }
}

/**
 * Get pricing info for a digital product (public)
 */
async function getProductPricing(slug: string): Promise<ApiResponse<DigitalProductPricing>> {
    try {
        const response = await getApiInstance().get(`/digital-products/${slug}/pricing`)
        const pricing = response.data?.data || response.data

        return {
            success: true,
            data: pricing
        }
    } catch (error: any) {
        console.error('Error fetching digital product pricing:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch pricing'
        }
    }
}
